import { useMemo, useCallback } from 'react';
import Image from 'next/image';
import { cn } from '@restworld/utils-common';
import * as RadixDropdown from '@radix-ui/react-dropdown-menu';
import 'react-phone-number-input/style.css';
// The mobile metadata, like form-validators: one libphonenumber metadata set per bundle (RW-235, item 4).
import { getCountries, Country as CountryType } from 'react-phone-number-input/input-mobile';
//@ts-expect-error - no types available
import countryNames from 'react-phone-number-input/locale/it';
import { getCountryCallingCode } from 'react-phone-number-input/mobile';

const selectOptionsStyleClasses =
  'max-h-[305px] xl:max-h-[360px] overflow-auto rounded-lg z-[200] bg-neutral-10 p-2 text-base border border-neutral-90 shadow-[0px_4px_16px_0px_rgba(0,0,26,0.25)]';
const selectOptionStyleClasses =
  'relative cursor-pointer select-none py-0 px-5 h-12 flex items-center text-base text-neutral-700 font-medium hover:bg-neutral-30 hover:rounded-lg outline-none';

export function CountriesMenu({
  onChange,
  onSelect,
  value: valueProp,
  defaultCountry,
  favorites,
  wrapperStyle,
  dropdownPlacementConfig,
  variant = 'default',
  disabled,
}: CountriesMenuProps) {
  const value = valueProp ?? defaultCountry ?? ('IT' as Country);
  const handleChange = onChange ?? onSelect;

  const handleSelection = useCallback(
    (newValue: Country) => {
      handleChange && handleChange(newValue);
    },
    [handleChange]
  );

  const countries = useMemo(() => filterCountries(getCountries(), favorites), [favorites]);
  const sideOffset = dropdownPlacementConfig?.mainAxis ?? 8;

  return (
    <span className={cn(wrapperStyle)}>
      <RadixDropdown.Root>
        <RadixDropdown.Trigger asChild>
          <button
            disabled={disabled}
            className={cn(
              'inline-flex items-center p-2 h-8 w-fit gap-1 text-sm font-medium text-black rounded bg-neutral-40',
              disabled && 'cursor-not-allowed opacity-60'
            )}
          >
            <span>{renderFlag(value)}</span>
            <span>{variant === 'phone' ? `+${getCountryCallingCode(value)}` : value}</span>
          </button>
        </RadixDropdown.Trigger>
        <RadixDropdown.Portal>
          <RadixDropdown.Content
            side="bottom"
            align="start"
            sideOffset={sideOffset}
            className={cn(selectOptionsStyleClasses, 'w-[320px]')}
          >
            {countries?.map((country) => (
              <RadixDropdown.Item
                key={country}
                onSelect={() => handleSelection(country)}
                className={cn(
                  selectOptionStyleClasses,
                  'flex items-center gap-2',
                  'data-[highlighted]:bg-neutral-30 data-[highlighted]:rounded-lg'
                )}
              >
                <div
                  className={cn(
                    'flex w-full items-center gap-x-1 py-2 px-4 text-left text-sm',
                    !!favorites?.includes(country) &&
                      favorites[favorites.length - 1] === country &&
                      'border-b border-gray-200'
                  )}
                >
                  <span>{renderFlag(country)}</span>
                  <span>{countryNames[country]}</span>
                </div>
              </RadixDropdown.Item>
            ))}
          </RadixDropdown.Content>
        </RadixDropdown.Portal>
      </RadixDropdown.Root>
    </span>
  );
}
export default CountriesMenu;

export type Country = CountryType;

export interface CountriesMenuProps {
  onChange?: (value: Country) => void;
  /** @deprecated Use `onChange` instead */
  onSelect?: (value: Country) => void;
  value?: Country;
  /** @deprecated Use `value` instead */
  defaultCountry?: Country;
  favorites?: Country[];
  variant?: 'default' | 'phone';
  wrapperStyle?: string;
  dropdownPlacementConfig?: { mainAxis?: number; crossAxis?: number };
  disabled?: boolean;
}

export const renderFlag = (country: Country) => (
  <Image
    src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${country}.svg`}
    alt="flag icon"
    height={11}
    width={16}
  />
);

const filterCountries = (countries: Country[], favorites: Country[] | undefined): Country[] => {
  if (!favorites || favorites.length === 0) return countries;
  countries = countries.filter((country) => !favorites.includes(country));
  for (let i = favorites.length - 1; i >= 0; i--) {
    countries.unshift(favorites[i]);
  }

  return countries;
};
